import { Tooltip } from "@heroui/react";
import ColoredDot from "../operationalDashboard/components/coloredDot";

const dotColor = {
    red: "bg-brick-primary",
    orange: "bg-orange-500",
    green: "bg-moss-primary",
    transparent: "bg-gray-300"
  };

// Get the color name in Dutch
const getColorNameInDutch = (color: any) => {
    switch (color) {
      case 'green': return 'Goed';
      case 'orange': return 'Let op';
      case 'red': return 'Actie nodig';
      case 'transparent': return 'Niet van toepassing';
      default: return color;
    }
  };

const EnhancedTooltipContent = ({ tooltipContent, color, subTitle }: any) => {
    // Function to directly print the current tooltip
    const handlePrint = (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      // Create a new window with just the tooltip content
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Pop-up blocker heeft voorkomen dat het afdrukvenster wordt geopend. Sta pop-ups toe voor deze site.');
        return;
      }

      // Get current date for header
      const currentDate = new Date().toLocaleDateString('nl-NL');

      // Generate HTML for the print window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Status Overzicht - ${subTitle || ''}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #31403B;
              }
              .header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 20px;
                padding-bottom: 10px;
                border-bottom: 1px solid #838C89;
              }
              .category {
                margin-bottom: 20px;
              }
              .category-title {
                font-weight: bold;
                font-size: 16px;
                padding-bottom: 5px;
                margin-bottom: 10px;
                border-bottom: 1px solid #838C89;
              }
              .section-title {
                font-weight: bold;
                margin-top: 10px;
                margin-bottom: 5px;
              }
              .status-dot {
                display: inline-block;
                width: 10px;
                height: 10px;
                border-radius: 50%;
                margin-right: 8px;
              }
              .status-dot.green { background-color: #5B7304; }
              .status-dot.orange { background-color: #FFA500; }
              .status-dot.red { background-color: #EA8162; }
              .status-dot.transparent { background-color: #D3D3D3; }
              ul {
                list-style-type: none;
                padding-left: 5px;
                margin-top: 5px;
              }
              li {
                display: flex;
                align-items: flex-start;
                margin-bottom: 5px;
              }
              .footer {
                margin-top: 30px;
                padding-top: 10px;
                border-top: 1px solid #838C89;
                text-align: center;
                color: #838C89;
              }
              @page {
                size: portrait;
                margin: 2cm;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Status Overzicht: ${subTitle || ''}</h1>
              <div>${currentDate}</div>
            </div>
            
            <div>
              <h2>${getColorNameInDutch(color)} Status Items</h2>
              
              ${tooltipContent.map((categoryGroup: any) => `
                <div class="category">
                  <div class="category-title">${categoryGroup.category}</div>
                  
                  ${categoryGroup.buildingItems.length > 0 ? `
                    <div>
                      <div class="section-title">Gemeenschappelijke delen:</div>
                      <ul>
                        ${categoryGroup.buildingItems.map((item: any) => `
                          <li>
                            <span class="status-dot ${color}"></span>
                            <div>${item.statusText}</div>
                          </li>
                        `).join('')}
                      </ul>
                    </div>
                  ` : ''}
                  
                  ${Object.entries(categoryGroup.unitItemsByStatus).length > 0 ? `
                    <div>
                      <div class="section-title">Units:</div>
                      <ul>
                        ${Object.entries(categoryGroup.unitItemsByStatus).map(([status, items]: any) => `
                          <li>
                            <span class="status-dot ${color}"></span>
                            <div>
                              <span style="font-weight: bold">${status}</span>
                              <span style="color: #838C89">
                                (voor ${items.length} ${items.length === 1 ? 'unit' : 'units'}: 
                                ${items.map((item: any) => ` ${item.name}`).join(', ')})
                              </span>
                            </div>
                          </li>
                        `).join('')}
                      </ul>
                    </div>
                  ` : ''}
                </div>
              `).join('')}
            </div>
            
            <div class="footer">
              Yally - Due Diligence Status Rapport
            </div>
          </body>
        </html>
      `);

      // Close the document and trigger print
      printWindow.document.close();
      printWindow.onload = function () {
        printWindow.focus();
        printWindow.print();
      };
    };

    if (!tooltipContent || tooltipContent.length === 0) {
      return <div className="text-sm p-2">Geen details beschikbaar</div>;
    }

    return (
      <div className="max-h-96 overflow-auto">
        <div className="flex justify-between items-center p-2 border-b border-neutral-secondary-mid">
          <h3 className="font-bold text-neutral-primary">Status Overzicht</h3>
          <button
            onClick={handlePrint}
            className="px-3 py-1 bg-moss-primary text-white text-sm rounded-md hover:bg-opacity-90 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Afdrukken
          </button>
        </div>

        <div className="p-2">
          {tooltipContent.map((categoryGroup: any, index: any) => (
            <div key={index} className="mb-4">
              <h3 className="font-bold border-b border-neutral-secondary-mid pb-1 mb-2">
                {categoryGroup.category}
              </h3>

              {/* Building (Common Areas) Items */}
              {categoryGroup.buildingItems.length > 0 && (
                <div className="mb-2">
                  <h4 className="font-semibold text-neutral-primary">Gemeenschappelijke delen:</h4>
                  <ul className="list-none pl-1 mb-2">
                    {categoryGroup.buildingItems.map((item: any, idx: any) => (
                      <li key={idx} className="text-sm mb-1 flex items-start">
                        <span className={`inline-block w-3 h-3 rounded-full mr-2 mt-1 ${dotColor[color as keyof typeof dotColor]}`}></span>
                        <div>{item.statusText}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Unit Items */}
              {Object.entries(categoryGroup.unitItemsByStatus).length > 0 && (
                <div>
                  <h4 className="font-semibold text-neutral-primary">Units:</h4>
                  <ul className="list-none pl-1">
                    {Object.entries(categoryGroup.unitItemsByStatus).map(([status, items]: any, idx: any) => (
                      <li key={idx} className="text-sm mb-1 flex items-start">
                        <span className={`inline-block w-3 h-3 rounded-full mr-2 mt-1 ${dotColor[color as keyof typeof dotColor]}`}></span>
                        <div>
                          <span className="font-medium">{status}</span>
                          <span className="text-neutral-secondary-mid ml-1">
                            (voor {items.length} {items.length === 1 ? 'unit' : 'units'}:
                            {items.map((item: any) => ` ${item.name}`).join(', ')})
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

const StatusTooltip = ({numericValues, subTitle, color}: any) => {
    /*
    * Spherical dots are often used to represent statuses of various tasks that need to be completed.
    */
    return (
        <Tooltip
        content={
            <EnhancedTooltipContent
            tooltipContent={numericValues[color].tooltipContent || []}
            color={color}
            subTitle={subTitle}
            />
        }
        placement="bottom"
        classNames={{
            content: "p-0 max-w-md w-[450px]"
        }}
        >
            <div>
                <ColoredDot
                color={color}
                number={numericValues[color].count || numericValues[color].sources.length}
                labels={numericValues[color].labels || []}
                sources={numericValues[color].sources || []}
                hideLabels={true}
                />
            </div>
        </Tooltip>
    )
}

export default StatusTooltip;